import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthContext from "../../services/authContext";
import "./styles.css";

const ACCENTURE_URL =
  "https://login.microsoftonline.com/c9722d70-8b40-4d3b-810b-20095bb4cffc/oauth2/v2.0/authorize?response_type=code&client_id=7fd4bbae-3057-4042-b793-ce47ce54fdef&scope=https%3A%2F%2Fgraph.microsoft.com%2Foffline_access%20openid&redirect_uri=https%3A%2F%2Freport.acnasb.com";

const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL || "/api/";
const LOGIN_BOOTSTRAP_URL = REACT_APP_BASE_URL + "web/login/bootstrap";

const PRISM_TRANSFORMS = {
  front: "translateZ(-100px)",
  back: "translateZ(-100px) rotateY(-180deg)",
  right: "translateZ(-100px) rotateY(-90deg)",
  bottom: "translateZ(-100px) rotateX(90deg)",
};

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${src}"]`);

    if (existingScript) {
      if (existingScript.dataset.loaded === "true") {
        resolve();
        return;
      }

      existingScript.addEventListener("load", resolve, { once: true });
      existingScript.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.defer = true;
    script.dataset.loaded = "false";
    script.addEventListener(
      "load",
      () => {
        script.dataset.loaded = "true";
        resolve();
      },
      { once: true }
    );
    script.addEventListener("error", reject, { once: true });
    document.body.appendChild(script);
  });
}

function LoginPage() {
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState("front");
  const [bootstrap, setBootstrap] = useState({
    googleApiKey: "",
    captchaImage: "",
    cpaws: "",
  });
  const [form, setForm] = useState({
    username: "",
    password: "",
    captcha: "",
    smsCode: "",
    sessionInfo: "",
    sessionId: "",
  });
  const [statusMessage, setStatusMessage] = useState("");
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const loginExecutorRef = useRef(null);

  const isRecaptchaConfigured = bootstrap.googleApiKey.trim() !== "";

  useEffect(() => {
    if (auth.status === "OK") {
      navigate("/newweb", { replace: true });
    }
  }, [auth.status, navigate]);

  useEffect(() => {
    document.body.classList.add("login-page-body");

    return () => {
      document.body.classList.remove("login-page-body");
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.has("q")) {
      setView("back");
    }
  }, [location.search]);

  useEffect(() => {
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      window.alert("Você não pode usar o botão de voltar nesta página.");
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const bootstrapLogin = async () => {
    setIsBootstrapping(true);
    setIsRecaptchaReady(false);

    try {
      const response = await fetch(LOGIN_BOOTSTRAP_URL, {
        credentials: "include", 
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(`Bootstrap HTTP ${response.status}`);
      }

      const data = await response.json();

      setBootstrap({
        googleApiKey: data.googleApiKey || "",
        captchaImage: data.captchaImage || "",
        cpaws: data.cpaws || "",
      });

      if ((data.googleApiKey || "").trim() === "") {
        setStatusMessage("GOOGLE_API_KEY nao configurada no backend.");
        return;
      }

      setStatusMessage("");
    } catch (error) {
      setStatusMessage("Falha ao carregar o login. Atualize a página.");
    } finally {
      setIsBootstrapping(false);
    }
  };

  useEffect(() => {
    bootstrapLogin();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const setupRecaptcha = async () => {
      if (!bootstrap.googleApiKey) {
        setIsRecaptchaReady(false);
        return;
      }

      try {
        await loadScript("https://www.google.com/recaptcha/api.js");
        await loadScript("https://www.gstatic.com/firebasejs/10.12.2/firebase-app-compat.js");
        await loadScript("https://www.gstatic.com/firebasejs/10.12.2/firebase-auth-compat.js");
        // await loadScript("https://www.gstatic.com/firebasejs/7.6.1/firebase-app.js");
        // await loadScript("https://www.gstatic.com/firebasejs/7.6.1/firebase-auth.js");

        if (cancelled || !window.firebase?.auth) {
          return;
        }

        if (!window.firebase.apps.length) {
          window.firebase.initializeApp({
            apiKey: bootstrap.googleApiKey,
          });
        }

        if (window.recaptchaVerifier?.clear) {
          window.recaptchaVerifier.clear();
        }

        window.recaptchaVerifier = new window.firebase.auth.RecaptchaVerifier(
          "login-recaptcha",
          {
            size: "invisible",
            callback: (recaptchaToken) => {
              if (typeof loginExecutorRef.current === "function") {
                loginExecutorRef.current(recaptchaToken);
              }
            },
          }
        );

        await window.recaptchaVerifier.render();

        if (!cancelled) {
          setIsRecaptchaReady(true);
        }
      } catch (error) {
        if (!cancelled) {
          setStatusMessage("Falha ao inicializar o reCAPTCHA.");
          setIsRecaptchaReady(false);
        }
      }
    };

    setupRecaptcha();

    return () => {
      cancelled = true;
    };
  }, [bootstrap.googleApiKey]);

  console.log("Bootstrap data:", bootstrap);

  const updateField = (field) => (event) => {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: event.target.value,
    }));
  };

  const resetRecaptcha = () => {
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.render().then((widgetId) => {
        window.grecaptcha?.reset(widgetId);
      });
    }
  };

  const redirectToNewWebWithReload = () => {
    const targetUrl = `/newweb?_r=${Date.now()}`;
    window.location.replace(targetUrl);
  };

  const executeLogin = async (recaptchaToken) => {
    setIsSubmitting(true);

    try {
      const payload = new URLSearchParams({
        type: "login",
        user: form.username,
        pass: form.password,
        captcha: form.captcha,
        cpaws: bootstrap.cpaws,
        recaptcha: recaptchaToken,
      });

      const response = await fetch(REACT_APP_BASE_URL + "core/ajax/login", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: payload.toString(),
      });
      const result = await response.json();

      if (result.code !== 200) {
        if (result.e === "USER_DISABLED") {
          window.location.href = "/newweb/login?msg=User_Disabled";
          return;
        }

        const sanitizedMessage = (result.msg || "Fail Login")
          .replace(/ /g, "_")
          .replace(/[^a-zA-Z0-9_]/g, "");
        window.location.href = `/newweb/login?msg=Error_Auth_${result.code}_${sanitizedMessage}`;
        return;
      }

      if ((result.sessionInfo || "").includes("TOO_MANY_ATTEMPTS_TRY_LATER")) {
        setStatusMessage("TOO MANY ATTEMPTS! TRY LATER...");
        return;
      }

      if ((result.sessionInfo || "").includes("CAPTCHA_CHECK_FAILED")) {
        setStatusMessage("CAPTCHA CHECK FAILED! TRY AGAIN...");
        await bootstrapLogin();
        return;
      }

      setForm((currentForm) => ({
        ...currentForm,
        sessionInfo: result.sessionInfo || "",
        sessionId: result.session_id || "",
      }));
      setStatusMessage("");
      setView("right");
    } catch (error) {
      setStatusMessage("Falha ao autenticar. Tente novamente.");
    } finally {
      resetRecaptcha();
      setIsSubmitting(false);
    }
  };

  loginExecutorRef.current = executeLogin;

  const handleLoginClick = async () => {
    if (!form.username || !form.password || !form.captcha) {
      setStatusMessage("Preencha usuario, senha e captcha.");
      return;
    }

    if (!isRecaptchaConfigured) {
      setStatusMessage("GOOGLE_API_KEY nao configurada no backend.");
      return;
    }

    if (!isRecaptchaReady || !window.recaptchaVerifier) {
      setStatusMessage("reCAPTCHA indisponivel. Atualize a pagina.");
      return;
    }

    setStatusMessage("");
    setIsSubmitting(true);

    try {
      await window.recaptchaVerifier.verify();
    } catch (error) {
      setStatusMessage("Falha ao validar o reCAPTCHA.");
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!form.smsCode || !form.sessionInfo) {
      setStatusMessage("Informe o codigo SMS.");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = new URLSearchParams({
        type: "chechCode",
        sessionInfo: form.sessionInfo,
        code: form.smsCode,
        phpsession: form.sessionId,
      });

      const response = await fetch(REACT_APP_BASE_URL + "core/ajax/login", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: payload.toString(),
      });
      const result = await response.text();
      const [status, details] = result.split(":", 2);

      if (status === "INVALID_CODE") {
        setStatusMessage("INVALID CODE");
        return;
      }

      if (status === "OK") {
        setView("bottom");
        window.setTimeout(() => {
          redirectToNewWebWithReload();
        }, 150);
        return;
      }

      setStatusMessage(details || result);
    } catch (error) {
      setStatusMessage("Falha ao validar o codigo SMS.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="wrapper">
        <div className="rec-prism" style={{ transform: PRISM_TRANSFORMS[view] }}>
          <div className="face face-top" />

          <div className="face face-front">
            <div className="content">
              <h2>Login Type</h2>
              <br />
              <button onClick={() => setView("back")}>Telefónica</button>
              <br />
              <br />
              <button onClick={() => window.open(ACCENTURE_URL, "_self")}>Accenture</button>
            </div>
          </div>

          <div className="face face-back">
            <div className="content">
              <h2>ASB B2C</h2>
              <form onSubmit={(event) => event.preventDefault()}>
                <div className="field-wrapper">
                  <input
                    type="text"
                    id="username"
                    name="username"
                    placeholder="username"
                    autoComplete="off"
                    value={form.username}
                    onChange={updateField("username")}
                  />
                  <label>username</label>
                </div>
                <div className="field-wrapper">
                  <input
                    type="password"
                    id="password"
                    name="password"
                    placeholder="password"
                    autoComplete="off"
                    value={form.password}
                    onChange={updateField("password")}
                  />
                  <label>password</label>
                </div>
                <div className="field-wrapper captcha-row">
                  <img
                    src={bootstrap.captchaImage}
                    alt="Math captcha"
                    valign="middle"
                    height="30"
                  />
                  <small>
                    <button
                      type="button"
                      className="refresh-link"
                      onClick={bootstrapLogin}
                    >
                      Update math
                    </button>
                  </small>
                </div>
                <div className="field-wrapper">
                  <input
                    type="number"
                    min="0"
                    id="captcha"
                    name="captcha"
                    placeholder="Answer here"
                    autoComplete="off"
                    value={form.captcha}
                    onChange={updateField("captcha")}
                  />
                  <label>Answer here</label>
                </div>
                <div className="field-wrapper">
                  <input
                    type="submit"
                    id="login-submit"
                    value={isSubmitting ? "Loading..." : "Submit"}
                    onClick={handleLoginClick}
                    disabled={isBootstrapping || isSubmitting}
                  />
                </div>
              </form>
            </div>
          </div>

          <div className="face face-right">
            <div className="content">
              <h2>We texted your phone a SMS code. Please enter the code to sign in.</h2>
              <form onSubmit={(event) => event.preventDefault()}>
                <div className="field-wrapper">
                  <input
                    type="number"
                    min="0"
                    name="text"
                    placeholder="Insert SMS code"
                    id="coded"
                    autoComplete="off"
                    value={form.smsCode}
                    onChange={updateField("smsCode")}
                  />
                  <label>SMS Code</label>
                </div>
                <div className="field-wrapper" />

                <div className="field-wrapper">
                  <input
                    type="submit"
                    onClick={handleVerifyCode}
                    value={isSubmitting ? "Loading..." : "Check"}
                  />
                </div>
              </form>
            </div>
          </div>

          <div className="face face-left" />

          <div className="face face-bottom">
            <div className="content">
              <div className="thank-you-msg">Thank you!</div>
            </div>
          </div>
        </div>
        <div id="retornologin">{statusMessage}</div>
      </div>
      <div id="login-recaptcha" className="login-recaptcha" />
    </div>
  );
}

export default LoginPage;